<template>
    <div>
        <template v-if="question.options && question.options.length">
            <CardSelect
                :model-value="answer"
                :required="!!question.required"
                :name="questionInputName"
                :label="questionLabel"
                :type="question.allowMultipleAnswers ? 'checkbox' : 'radio'"
                :force-array-usage-for-radio-input="true"
                :custom-input-rules="questionRules"
                :allow-custom-answers="!!question.freeTextAllowed"
                @update:model-value="emitEvent($event)">
                <AnswerSelectItem
                    v-for="option in question.options"
                    :key="option.uuid"
                    :option="option"
                    :question-key="question.uuid"
                    :person-key="personKey"
                    :answer="answer"
                    :registrar-mode="registrarMode" />
            </CardSelect>
        </template>
        <template v-else>
            <template v-if="question.questionType === questionTypes.TYPE_QUANTITY">
                <base-input
                    :name="questionInputName"
                    :label="questionLabel"
                    inputmode="numeric"
                    :disabled="notAvailable"
                    :rules="{numeric: true, min_value: 0, max_value: availabilityInRegistration === null ? 99999999999999999999 : availabilityInRegistration}"
                    :required="!!question.required && !(notAvailable)"
                    :model-value="answer ? (Array.isArray(answer) ? answer[0]: answer) : ''"
                    @update:model-value="emitEvent([$event])" />
                <small
                    v-if="notAvailable"
                    class="text-muted">{{
                        $t('eventRegistration.questions.notAvailable')
                    }}</small>
            </template>
            <template v-else-if="question.questionType === questionTypes.TYPE_DATE">
                <base-input
                    :name="questionInputName"
                    :label="questionLabel"
                    type="date"
                    :disabled="notAvailable"
                    :required="!!question.required && !(notAvailable)"
                    :model-value="answer ? (Array.isArray(answer) ? answer[0]: answer) : ''"
                    @update:model-value="emitEvent([$event])" />
                <small
                    v-if="notAvailable"
                    class="text-muted">{{
                        $t('eventRegistration.questions.notAvailable')
                    }}</small>
            </template>
            <template v-else>
                <base-input
                    :name="questionInputName"
                    :label="questionLabel"
                    show-max-rule-counter
                    :required="!!question.required"
                    :rules="checkedCustomRules"
                    :model-value="answer ? (Array.isArray(answer) ? answer[0]: answer) : ''"
                    @update:model-value="emitEvent([$event])" />
            </template>
        </template>
    </div>
</template>

<script>
import AnswerSelectItem from "@/components/pages/registration/questions/AnswerSelectItem";
import questionTypes from "@/util/consts/questionTypes";
import customRulesMixin from "./customRulesMixin";
import mainRegistrationMixin from "@/mixins/registration/mainRegistrationMixin";

export default {
    name: "QuestionAnswers",
    components: {AnswerSelectItem},
    mixins: [customRulesMixin, mainRegistrationMixin],
    props: {
        answer: {
            required: true
        },
        personKey: {
            type: [String, Number],
            required: true
        },
        question: {
            type: Object,
            required: true
        },
        personName: {},
        registrarMode: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            questionTypes
        }
    },
    computed: {
        questionRules() {
            const baseRules = {max: 100}
            const validation = this.question.answerValidation
            if (!validation) return baseRules // Should not happen if store is correctly loaded
            let additionalRules = {}
            switch (validation.category) {
                case 'text':
                    additionalRules = this.getTextRule(validation)
                    break
                case 'number':
                    additionalRules = this.getNumberRule(validation)
                    break
                case 'regex':
                    additionalRules = this.getRegexRule(validation)
                    break
                case 'iban':
                    additionalRules = this.getIbanRule(validation)
            }

            return {...baseRules, ...additionalRules}
        },
        uncheckedRules() {
            return this.questionRules
        },
        getQuestionAnswerSelectionInRegistration() {
            return this.$store.getters['registration/getQuestionAnswerSelectionInRegistration'](this.question.uuid)
        },
        availabilities() {
            return this.$store.state?.registration?.availabilities ?? {}
        },
        availability() {
            return this.availabilities[this.question?.uuid] ?? null
        },
        reservedAvailabilities() {
            return this.$store.state?.registration?.reservedAvailabilities ?? {}
        },
        reservedAvailability() {
            return this.reservedAvailabilities[this.question?.uuid] ?? 0
        },
        restrictedByAvailability() {
            return this.availability !== null
        },
        selectedAnswerValue() {
            return Array.isArray(this.answer) ? this.answer[0] : this.answer
        },
        availabilityInRegistration() {
            if (!this.restrictedByAvailability) return null
            const availabilityWithoutCurrentAnswer = this.availability
                - this.reservedAvailability
                - this.getQuestionAnswerSelectionInRegistration
                + parseInt(this.selectedAnswerValue ?? 0)
            return availabilityWithoutCurrentAnswer < 0 ? 0 : availabilityWithoutCurrentAnswer
        },
        notAvailable() {
            return this.availabilityInRegistration != null && this.availabilityInRegistration <= 0
        },
        person() {
            return this.registrationData.persons[this.personKey]
        },
        questionLabel() {
            if (this.question?.questionType === questionTypes.TYPE_QUANTITY) {
                return this.personName ?
                    this.$t('eventRegistration.questions.quantityAnswerFrom') + ' ' + this.personName :
                    this.$t('eventRegistration.questions.yourQuantityAnswer')
            } else if (this.question?.questionType === questionTypes.TYPE_DATE) {
                return this.personName ?
                    this.$t('eventRegistration.questions.dateAnswerFrom') + ' ' + this.personName :
                    this.$t('eventRegistration.questions.yourDateAnswer')
            }
            return this.personName ?
                this.$t('eventRegistration.questions.answerFrom') + ' ' + this.personName :
                this.$t('eventRegistration.questions.yourAnswer')
        },
        questionInputName() {
            return (this.question?.uuid + ".") + (this.questionLabel) + " zu " + "\"" + (this.question?.questionText ?? "")?.replaceAll('.', '') + "\""
        }
    },
    methods: {
        emitEvent(answer) {
            this.$emit('event', {
                answer: answer,
                personKey: this.personKey,
                questionUuid: this.question.uuid
            })
        },
        getTextRule(validation) {
            switch (validation.operator) {
                case 'mail':
                    return {
                        email: true
                    }
                case 'minLength':
                    return {
                        min: validation.numeric_value
                    }
                case 'maxLength':
                    return {
                        max: validation.numeric_value
                    }
                case 'isPhoneNumber':
                    return {
                        phoneInvalid: false
                    }
                case 'juleicaNumber':
                    return {
                        juleicaInvalid: false,
                        juleicaCardNumberDoesntMatchWithContact: false
                    }
                case 'includes':
                    return {
                        includes: {
                            text: validation.string_value,
                            message: validation.error_message
                        }
                    }
                default:
                    return {}
            }
        },
        getNumberRule(validation) {
            switch (validation.operator) {
                case 'isNumber':
                    return {
                        numeric: true
                    }
                case 'greaterThan':
                    return {
                        min_value: validation.numeric_value
                    }
                case 'lessThan':
                    return {
                        max_value: validation.numeric_value
                    }
                case 'between':
                    return {
                        between: [validation.lower_bound, validation.upper_bound]
                    }
                default:
                    return {}
            }
        },
        getRegexRule(validation) {
            let regex
            try {
                regex = new RegExp(validation.string_value);
            } catch (e) {
                regex = new RegExp(".*")
            }
            return {
                regexWithMessage: {
                    regex: regex,
                    message: validation.error_message
                }
            }
        },
        getIbanRule() {
            return {
                ibanInvalid: false
            }
        }
    },
    watch: {
        answer: {
            handler(value) {
                if (!value) return
                if (Array.isArray(value))
                    this.checkCustomRulesFor(value[0])
                else
                    this.checkCustomRulesFor(value)
            }
        }
    }
}
</script>

<style scoped>

</style>
